import React, { useState, useRef, useEffect, KeyboardEvent } from 'react'
import CustomButton from './CustomButton'
import { SvgArroIconOnlyTop } from '../icons'
import Popover from './Popover'
import { DropdownType } from '../utils'
import PopupUiDropDownList from './PopupUiDropDownList'

interface DropDownListProps {
  dataQaId: string
  triggerButtonLabel: string
  filterOptions: DropdownType<string>[]
  handleSearch: (searchTerm: string) => void
  searchPlaceholder: string
  onApply?: (selectedOption: DropdownType<string> | null) => void
  onCancel?: () => void
  cancelButtonLabel: string
  applyButtonLabel: string
  showButton: boolean
  modalCss?: string
  optionCss?: string
  selectOption?: boolean
  optionListHeight?: string
  icon?: React.ReactNode
  arrowIcon?: boolean
  isDisabled?: boolean
  emptyDataMessage?: string
  onBtnClick?: () => void
}

const DropDownList: React.FC<DropDownListProps> = ({
  dataQaId,
  triggerButtonLabel,
  filterOptions,
  handleSearch,
  searchPlaceholder,
  onApply,
  onCancel,
  cancelButtonLabel,
  applyButtonLabel,
  showButton,
  modalCss,
  optionCss,
  selectOption,
  optionListHeight,
  icon,
  arrowIcon,
  isDisabled,
  emptyDataMessage,
  onBtnClick,
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [selectedOption, setSelectedOption] = useState<{
    label: string
    value: string
  } | null>(null)
  const [activeIndex, setActiveIndex] = useState(-1)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const searchContainerRef = useRef<HTMLDivElement>(null)
  const optionsRef = useRef<(HTMLLIElement | null)[]>([])
  const cancelButtonRef = useRef<HTMLButtonElement>(null)
  const applyButtonRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if (isOpen) {
      // Reset option refs array when options change
      optionsRef.current = optionsRef.current.slice(0, filterOptions.length)

      // Focus search input when dropdown opens
      setTimeout(() => {
        if (searchContainerRef.current) {
          const searchInput = searchContainerRef.current.querySelector('input')
          if (searchInput) {
            searchInput.focus()
          }
        }
      }, 0)
    } else {
      // Reset active index when closed
      setActiveIndex(-1)
    }
  }, [isOpen, filterOptions.length])

  const handleTriggerKeyDown = (e: KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === 'Enter' || e.key === 'ArrowDown' || e.key === ' ') {
      e.preventDefault()
      setIsOpen(true)
      onBtnClick && onBtnClick()
    }
  }

  const handleOptionKeyDown = (
    e: KeyboardEvent<HTMLLIElement>,
    index: number
  ) => {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        setActiveIndex((prevIndex) => {
          const nextIndex = (prevIndex + 1) % filterOptions.length
          optionsRef.current[nextIndex]?.focus()
          return nextIndex
        })
        break
      case 'ArrowUp':
        e.preventDefault()
        setActiveIndex((prevIndex) => {
          const nextIndex =
            prevIndex <= 0 ? filterOptions.length - 1 : prevIndex - 1
          optionsRef.current[nextIndex]?.focus()
          return nextIndex
        })
        break
      case 'Enter':
      case ' ': {
        e.preventDefault()
        const optionAtIndex = filterOptions[index]
        setSelectedOption(
          selectedOption?.value === optionAtIndex.value ? null : optionAtIndex
        )
        if (!showButton) {
          // If no buttons, auto-apply and close
          if (onApply) {
            const newSelected =
              selectedOption?.value === optionAtIndex.value
                ? null
                : optionAtIndex
            onApply(newSelected)
          }
          setIsOpen(false)
          buttonRef.current?.focus()
        } else if (cancelButtonRef.current) {
          // Move focus to cancel button
          cancelButtonRef.current.focus()
        }
        break
      }
      case 'Escape':
        e.preventDefault()
        setIsOpen(false)
        buttonRef.current?.focus()
        break
      case 'Tab':
        if (!showButton) {
          e.preventDefault()
          setIsOpen(false)
          buttonRef.current?.focus()
        }
        break
    }
  }

  const handleSearchKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        if (filterOptions.length > 0) {
          setActiveIndex(0)
          optionsRef.current[0]?.focus()
        }
        break
      case 'Escape':
        e.preventDefault()
        setIsOpen(false)
        buttonRef.current?.focus()
        break
    }
  }

  const handleCancelClick = () => {
    setIsOpen(false)
    setSelectedOption(null)
    if (onCancel) {
      onCancel()
    }
    buttonRef.current?.focus()
  }

  const handleApplyClick = () => {
    if (onApply) {
      onApply(selectedOption)
    }
    setIsOpen(false)
    buttonRef.current?.focus()
  }

  const closePopover = () => {
    setIsOpen(false)
    buttonRef.current?.focus()
  }

  return (
    <div className="relative inline-block">
      <Popover
        title={triggerButtonLabel}
        buttonFocusable={false}
        content={
          <PopupUiDropDownList
            handleSearch={handleSearch}
            searchPlaceholder={searchPlaceholder}
            filterOptions={filterOptions}
            onApply={handleApplyClick}
            onCancel={handleCancelClick}
            cancelButtonLabel={cancelButtonLabel}
            applyButtonLabel={applyButtonLabel}
            selectedOption={selectedOption}
            setSelectedOption={setSelectedOption}
            showButton={showButton}
            optionCss={optionCss}
            selectOption={selectOption}
            optionListHeight={optionListHeight}
            dataQaId={dataQaId}
            isDisabled={isDisabled}
            emptyDataMessage={emptyDataMessage}
            searchContainerRef={searchContainerRef}
            optionsRef={optionsRef}
            activeIndex={activeIndex}
            handleSearchKeyDown={handleSearchKeyDown}
            handleOptionKeyDown={handleOptionKeyDown}
            cancelButtonRef={cancelButtonRef}
            applyButtonRef={applyButtonRef}
          />
        }
        onClose={closePopover}
        cssClassName={modalCss}
        position="auto"
        buttonRef={buttonRef}
        isOpen={isOpen}
      >
        <CustomButton
          color="others"
          isFilled={false}
          className="group h-[2.375rem] bg-accent-0 text-black border-primary-500"
          widthClass="w-[9.5rem]"
          ref={buttonRef}
          onClick={(e) => {
            setIsOpen((prev) => !prev)
            onBtnClick && onBtnClick()
            e?.stopPropagation()
          }}
          onKeyDown={handleTriggerKeyDown}
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          onKeyDownCapture={(e) => {
            handleTriggerKeyDown(e)
          }}
        >
          <div
            className={`flex ${
              arrowIcon ? 'justify-between' : 'justify-center'
            } w-full items-center`}
          >
            <div className="flex gap-1 items-center">
              {icon}
              <span className="group-hover:text-primary-500">
                {triggerButtonLabel}
              </span>
            </div>
            {arrowIcon && (
              <SvgArroIconOnlyTop
                className={`transform transition-transform duration-200 group-hover:text-primary-500 ${
                  isOpen ? '' : 'rotate-180'
                } text-[#000000]`}
              />
            )}
          </div>
        </CustomButton>
      </Popover>
    </div>
  )
}

export default DropDownList
